/**
 * Generic scroll-snap carousel.
 *
 * CSS does the scrolling; this module only adds affordances. The controls are
 * hidden until it sets data-awdd-carousel-ready, so a dead bundle leaves a
 * swipeable, keyboard-reachable scroll row rather than buttons that do nothing.
 *
 * No autoplay and no loop: both are Swiper features the reference uses, and
 * neither earns its complexity for a handful of cards.
 */
export function initCarousels() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('[data-awdd-carousel]').forEach(root => {
        const track = root.querySelector('[data-awdd-carousel-track]');
        if (!track) return;

        const slides = Array.from(track.children);
        if (slides.length < 2) return;

        const prev = root.querySelector('[data-awdd-carousel-prev]');
        const next = root.querySelector('[data-awdd-carousel-next]');
        const dots = root.querySelector('[data-awdd-carousel-dots]');

        const scrollToSlide = i => {
            const target = slides[Math.max(0, Math.min(i, slides.length - 1))];
            track.scrollTo({
                left: target.offsetLeft - track.offsetLeft,
                behavior: reduceMotion ? 'auto' : 'smooth',
            });
        };

        const currentIndex = () => {
            const x = track.scrollLeft + track.offsetLeft;
            let best = 0;
            let bestDist = Infinity;
            slides.forEach((s, i) => {
                const d = Math.abs(s.offsetLeft - x);
                if (d < bestDist) { bestDist = d; best = i; }
            });
            return best;
        };

        // A track that shows more than one slide at a time has FEWER scroll
        // positions than slides: the last few slides share the end of the
        // range. This one is three-up at 1024px, so at 1440px only three of
        // its five offsets are reachable -- and one dot per slide left dots 4
        // and 5 inert, landing on the same position as dot 3 and never reading
        // as current. Derive the count from geometry instead: a slide is a
        // reachable position only while its offset is inside the scrollable
        // range. The count changes per breakpoint (5 dots at 390px, 4 at
        // 900px, 3 at 1440px), so this is recomputed on resize too.
        const pageCount = () => {
            const max = track.scrollWidth - track.clientWidth;
            let n = 0;
            slides.forEach(s => {
                if (s.offsetLeft - track.offsetLeft <= max + 1) n += 1;
            });
            return Math.max(1, n);
        };

        const renderDots = () => {
            if (!dots) return;
            const n = pageCount();
            if (dots.children.length === n) return;
            dots.textContent = '';
            for (let i = 0; i < n; i += 1) {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'awdd-carousel__dot';
                // Still "slide N": dot N scrolls slide N to the leading edge,
                // whatever else is visible beside it.
                b.setAttribute('aria-label', `Go to slide ${i + 1}`);
                b.addEventListener('click', () => scrollToSlide(i));
                dots.appendChild(b);
            }
        };

        const sync = () => {
            const i = currentIndex();
            if (dots) {
                Array.from(dots.children).forEach((d, di) => {
                    d.setAttribute('aria-current', di === i ? 'true' : 'false');
                });
            }
            if (prev) prev.disabled = track.scrollLeft <= 1;
            if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
        };

        if (prev) prev.addEventListener('click', () => scrollToSlide(currentIndex() - 1));
        if (next) next.addEventListener('click', () => scrollToSlide(currentIndex() + 1));

        let raf = 0;
        track.addEventListener('scroll', () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(sync);
        }, { passive: true });

        // A breakpoint change can turn a scrollable track into a non-scrollable
        // one (or back), which changes scrollWidth/clientWidth without firing a
        // scroll event -- so prev/next's disabled state otherwise goes stale
        // across a resize. Same rAF throttle as the scroll listener above.
        window.addEventListener('resize', () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => { renderDots(); sync(); });
        }, { passive: true });

        root.setAttribute('data-awdd-carousel-ready', '');
        renderDots();
        sync();
    });
}
